import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import SiteFooter from "@/components/SiteFooter";


const passwordSchema = z.object({
  password: z.string().trim().min(1, { message: "Le mot de passe est requis" }).max(100, { message: "Le mot de passe doit faire moins de 100 caractères" }),
});

const PasswordProtection = () => {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate input
      const validation = passwordSchema.safeParse({ password });
      if (!validation.success) {
        const errors = validation.error.errors.map(err => err.message).join(", ");
        toast({
          variant: "destructive",
          title: "Validation échouée",
          description: errors,
        });
        setIsLoading(false);
        return;
      }

      // Call edge function with credentials to set httpOnly cookie
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/validate-site-password`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ password: validation.data.password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || 'Erreur de validation');
      }

      if (data?.success) {
        toast({
          title: "Accès autorisé",
          description: "Bienvenue sur PartXplorer",
        });

        // Fallback pour navigateurs mobiles qui bloquent les cookies tiers (iOS Safari, etc.)
        localStorage.setItem('site_password_authenticated', 'true');

        // Redirect to home or intended destination
        const intendedPath = sessionStorage.getItem('intended_path') || '/';
        sessionStorage.removeItem('intended_path');
        
        // Use window.location to force full reload
        window.location.href = intendedPath;
      } else {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: data?.error || "Mot de passe incorrect",
        });
      }
    } catch (error: any) {
      console.error('Password validation error:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Une erreur est survenue",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">Site Protégé</CardTitle>
          <CardDescription className="text-center">
            Veuillez entrer le mot de passe pour accéder à PartXplorer
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !password}
            >
              {isLoading ? "Vérification..." : "Accéder au site"}
            </Button>
          </form>
        </CardContent>
      </Card>
      <SiteFooter />
    </div>
  );


};

export default PasswordProtection;
